import {
  Box,
  Stack,
  Body,
  LoadingButton,
  Loader,
  Card,
  Space,
  Launcher,
  BankIcon,
  ReadOnlyField,
  unsafeLocalizedString,
  Divider,
  useComponentsWrapperContext,
} from "@dbn/omnichannel-design-system-g4p";
import { RuntimeLocaleKey, useFormatMessage } from "../../intl";
import { TaskEither } from "fp-ts/TaskEither";
import { CoApplicantInput, UploadDocumentFlowType } from "../../globalDomain";
import { palette } from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";
import { usePortalStatusContext } from "../../PortalStatusContext";
import {
  constant,
  constFalse,
  constNull,
  constTrue,
  constUndefined,
  pipe,
} from "fp-ts/function";
import { option } from "fp-ts";
import { AddressWrite, AssignedUser } from "../domain";
import * as api from "../api";
import { useState } from "react";
import { useQuery } from "../../useAPI";
import * as remoteData from "../../RemoteData";
import { Option } from "fp-ts/Option";
import { ApplicationBranchDialog } from "./ApplicationBranchDialog";
import { PersonalDataProcessingDisclaimer } from "../../Common/PersonalDataProcessingDisclaimer/PersonalDataProcessingDisclaimer";
import { ReadOnlyFieldToValue } from "../../Common/ReadOnlyField/ReadOnlyFieldToValue";
type Props = {
  isWaitingForOpuAndCex?: boolean;
  onContinue: TaskEither<unknown, unknown>;
  onEditingChange: (isEditing: boolean) => void;
  productType: Option<UploadDocumentFlowType>;
  foreignSupport?: boolean;
} & CoApplicantInput;

export function BranchSelection(props: Props) {
  const formatMessage = useFormatMessage();
  const { portalBlocked } = usePortalStatusContext();
  const [assignedUserData, refreshAssignedData] = useQuery(api.getAssignedUser);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { componentType } = useComponentsWrapperContext();

  const unwrap = (value: Option<string>) =>
    pipe(value, option.getOrElse(constant("")));

  const isBrokerDataAvailable = (assignedUser: AssignedUser) =>
    option.isSome(assignedUser.brokerageCompany);

  const hasAssignedUser = foldAssignedUser(
    assignedUserData,
    constFalse,
    constFalse,
    constTrue,
    constFalse
  );

  const isG4p = !componentType || componentType === "g4p";
  const renderBranchData = (assignedUser: AssignedUser) => (
    <>
      <ReadOnlyFieldToValue
        size="medium"
        labelWeight={isG4p ? "bold" : undefined}
        value={`${unwrap(assignedUser.firstName)} ${unwrap(
          assignedUser.lastName
        )} ${unwrap(assignedUser.phoneNumber)}`}
        label={formatMessage(
          "Mortgage.Dashboard.Overview.branch.assignedBranch"
        )}
        cta={{
          label: formatMessage("Mortgage.Dashboard.Overview.branch.edit"),
          action: () => setDialogOpen(true),
        }}
        color={palette.dark}
      />
      {isG4p ? <Space units={4} /> : null}
      <Body size="medium" weight="regular">
        {unsafeLocalizedString(
          `${unwrap(assignedUser.branchName)} - ${unwrap(
            assignedUser.branchAddress
          )}`
        )}
      </Body>
    </>
  );

  const renderBrokerData = (assignedUser: AssignedUser) => (
    <>
      <ReadOnlyField
        size="medium"
        value={`${unwrap(assignedUser.firstName)} ${unwrap(
          assignedUser.lastName
        )} ${unwrap(assignedUser.phoneNumber)}`}
        label={formatMessage(
          "Mortgage.Dashboard.Overview.branch.assignedBroker"
        )}
      />
      <Body size="small" weight="regular">
        {unsafeLocalizedString(unwrap(assignedUser.brokerageCompany))}
      </Body>
    </>
  );

  const onSubmit = (): TaskEither<unknown, unknown> => {
    pipe(
      option.map<AddressWrite, api.IDAddressSubmitInput>(address => ({
        addressType: "Current",
        address,
        coApplicant: props.coApplicant,
      }))
    );

    return props.onContinue;
  };

  const renderCard = (assignedUser: AssignedUser) => (
    <Card grow>
      <Stack grow shrink column>
        {isBrokerDataAvailable(assignedUser)
          ? renderBrokerData(assignedUser)
          : renderBranchData(assignedUser)}
        <Space units={4} />
      </Stack>
    </Card>
  );

  const renderLauncher = () => (
    //!props.disabled && (
    <Launcher
      heading={formatMessage("Mortgage.Dashboard.Overview.branch.unassigned")}
      action={() => setDialogOpen(true)}
      buttonLabel={formatMessage("Mortgage.Dashboard.Overview.branch.proceed")}
      icon={BankIcon}
      variant="large"
    />
  );

  return (
    <Box grow hAlignContent="center">
      <Stack units={6} column grow>
        <Body size="medium" weight="regular" color={palette.neutral700}>
          {formatMessage("Identification.branchSelection.description")}
        </Body>
        {dialogOpen && (
          <ApplicationBranchDialog
            onDismiss={() => setDialogOpen(false)}
            onSubmit={() => {
              refreshAssignedData();
              setDialogOpen(false);
            }}
            initialValues={option.none}
          />
        )}
        {foldAssignedUser(
          assignedUserData,
          constant(
            <Loader
              ariaLabelMessage={formatMessage(
                "generic.loading.alt" as RuntimeLocaleKey
              )}
            />
          ),
          constNull,
          renderCard,
          renderLauncher
        )}
        <Divider width="100%" />
        <PersonalDataProcessingDisclaimer
          productType={pipe(
            props.productType,
            option.getOrElseW(constUndefined)
          )}
        />
        <Box hAlignContent="right">
          <LoadingButton
            variant="primary"
            size="default"
            labels={{
              normal: formatMessage(
                "Identification.additionalAddresses.continueWithoutAddresses"
              ),
              success: formatMessage(
                "Identification.additionalAddresses.continueWithoutAddresses"
              ),
              loading: formatMessage(
                "Identification.additionalAddresses.continueWithoutAddresses"
              ),
              error: formatMessage(
                "Identification.additionalAddresses.continueWithoutAddresses"
              ),
            }}
            action={onSubmit()}
            disabled={
              props.isWaitingForOpuAndCex || portalBlocked || !hasAssignedUser
            }
          />
        </Box>
      </Stack>
    </Box>
  );
}

function foldAssignedUser<R>(
  assignedUserRd: remoteData.RemoteData<unknown, AssignedUser>,
  onLoading: () => R,
  onError: () => R,
  onAssigned: (assignedUser: AssignedUser) => R,
  onUnassigned: () => R
): R {
  const isAssigned = (assignedUser: AssignedUser) =>
    option.isSome(assignedUser.firstName) ||
    option.isSome(assignedUser.lastName) ||
    option.isSome(assignedUser.phoneNumber);

  return pipe(
    assignedUserRd,
    remoteData.fold(onLoading, onError, assignedUser =>
      pipe(
        assignedUser,
        option.of,
        option.filter(isAssigned),
        option.map(onAssigned),
        option.getOrElse(onUnassigned)
      )
    )
  );
}
